/**
 * EmitirNotaCreditoModal
 *
 * Modal para emitir una Nota de Crédito sobre una factura CxC.
 * Solo CONTADOR puede emitir (el backend valida con 403; aquí solo ocultamos
 * el botón que abre este modal — ver InvoiceRow.js).
 *
 * El monto digitado por el usuario es siempre el TOTAL acreditado (equivalente
 * al modo "INCLUIDO" de calcularIva), igual que el total de la factura.
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import { calcularIva } from "../../shared/iva";

const OVERLAY = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.65)",
  zIndex: 1000,
  backdropFilter: "blur(3px)",
};

const MODAL_BASE = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%,-50%)",
  background: "var(--bg-card)",
  border: "1px solid var(--border-card)",
  borderRadius: 12,
  padding: "24px 24px 20px",
  width: "min(520px, 94vw)",
  zIndex: 1001,
  boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
};

const LABEL_STYLE = {
  display: "block",
  color: "var(--text-muted)",
  fontSize: "0.7rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  marginBottom: 4,
};

const INPUT_STYLE = {
  width: "100%",
  padding: "8px 10px",
  background: "var(--bg-input)",
  border: "1px solid var(--border-card)",
  borderRadius: 6,
  color: "var(--text-primary)",
  fontSize: "0.85rem",
  outline: "none",
  colorScheme: "light dark",
};

const MOTIVOS = [
  { valor: "DEVOLUCION", labelKey: "notasCredito.motivoDevolucion" },
  { valor: "CORRECCION", labelKey: "notasCredito.motivoCorreccion" },
  { valor: "DESCUENTO", labelKey: "notasCredito.motivoDescuento" },
  { valor: "ANULACION", labelKey: "notasCredito.motivoAnulacion" },
  { valor: "OTRO", labelKey: "notasCredito.motivoOtro" },
];

/** "2025-01-15" → "15/01/2025" */
function fmtDate(iso) {
  if (!iso || iso.length < 10) return iso ?? "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function todayISO() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export default function EmitirNotaCreditoModal({ invoice, onClose, onSuccess }) {
  const { t } = useTranslation();
  const facturaId = invoice.id_factura ?? invoice.id;
  const moneda = invoice.moneda || "CRC";
  const simbolo = moneda === "USD" ? "$" : "₡";

  const [loadingSaldo, setLoadingSaldo] = useState(true);
  const [saldoDisponible, setSaldoDisponible] = useState(0);
  const [notasPrevias, setNotasPrevias] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    motivo: "DEVOLUCION",
    concepto: "",
    monto: "",
    fecha: todayISO(),
  });

  useEffect(() => {
    let cancelled = false;
    apiFetch(`/api/finance/factura/${facturaId}/notas-credito/`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setSaldoDisponible(Number(data.saldo_disponible) || 0);
        setNotasPrevias(data.notas_credito ?? []);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingSaldo(false);
      });
    return () => {
      cancelled = true;
    };
  }, [facturaId]);

  function setField(k, v) {
    setForm((prev) => {
      const next = { ...prev, [k]: v };
      if (k === "motivo" && v === "ANULACION" && saldoDisponible > 0) {
        next.monto = String(saldoDisponible);
      }
      return next;
    });
  }

  const montoNum = Number(form.monto) || 0;
  const montoValido = montoNum > 0 && montoNum <= saldoDisponible;
  const conceptoValido = form.concepto.trim().length > 0;
  const { base, iva, total } = calcularIva(
    montoNum,
    invoice.tasa_iva,
    invoice.iva_incluido,
    "INCLUIDO",
  );

  async function handleSubmit() {
    if (!montoValido || !conceptoValido || submitting) return;
    setSubmitting(true);
    setError("");
    const res = await apiFetch("/api/finance/notaCreditoCxC/", {
      method: "POST",
      body: JSON.stringify({
        factura: facturaId,
        fecha: form.fecha,
        motivo: form.motivo,
        concepto: form.concepto.trim(),
        monto: montoNum,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      onSuccess?.(data);
      onClose();
    } else {
      const msg =
        data.monto?.[0] ??
        data.detail ??
        data.non_field_errors?.[0] ??
        t("notasCredito.errorEmitir");
      setError(msg);
    }
    setSubmitting(false);
  }

  return (
    <>
      <div onClick={onClose} style={OVERLAY} />
      <div
        style={{
          ...MODAL_BASE,
          width: "min(560px, 94vw)",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 16,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                background: "rgba(167,139,250,0.15)",
                borderRadius: 8,
                padding: "8px 10px",
                color: "#a78bfa",
              }}
            >
              <i className="fa-solid fa-file-circle-minus" style={{ fontSize: "1.1rem" }} />
            </div>
            <div>
              <h3
                style={{
                  margin: 0,
                  color: "var(--text-primary)",
                  fontSize: "1rem",
                  fontWeight: 700,
                }}
              >
                {t("notasCredito.emitirTitulo")}
              </h3>
              <p style={{ margin: "2px 0 0", color: "var(--text-muted)", fontSize: "0.78rem" }}>
                {invoice.numero_factura}
              </p>
            </div>
          </div>
          <CloseBtn onClick={onClose} />
        </div>
        <Divider />

        {/* Saldo disponible */}
        {loadingSaldo ? (
          <p style={{ color: "var(--text-secondary)", fontSize: "0.85rem", padding: "8px 0" }}>
            <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 6 }} />
            {t("notasCredito.cargandoSaldo")}
          </p>
        ) : (
          <WarningBox icon="fa-circle-info" color="#60a5fa">
            {t("notasCredito.saldoDisponible")}: {simbolo}
            {formatCurrency(saldoDisponible)}
          </WarningBox>
        )}

        {/* Notas de crédito previas */}
        {!loadingSaldo && notasPrevias.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <label style={LABEL_STYLE}>{t("notasCredito.notasPrevias")}</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {notasPrevias.map((nc) => (
                <div
                  key={nc.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: "var(--bg-input)",
                    border: "1px solid var(--border-card)",
                    borderRadius: 6,
                    padding: "6px 10px",
                    fontSize: "0.78rem",
                  }}
                >
                  <span style={{ fontWeight: 700, color: "var(--text-primary)" }}>
                    {nc.numero_nota}
                  </span>
                  <span style={{ color: "var(--text-secondary)" }}>
                    {fmtDate(nc.fecha)} · {t(MOTIVO_LABEL(nc.motivo))}
                  </span>
                  <span style={{ color: "#a78bfa", fontWeight: 600 }}>
                    {simbolo}
                    {formatCurrency(nc.monto)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Formulario */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 16px" }}>
          <div>
            <label style={LABEL_STYLE}>{t("notasCredito.motivo")}</label>
            <select
              style={{ ...INPUT_STYLE, cursor: "pointer" }}
              value={form.motivo}
              onChange={(e) => setField("motivo", e.target.value)}
            >
              {MOTIVOS.map((m) => (
                <option key={m.valor} value={m.valor}>
                  {t(m.labelKey)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={LABEL_STYLE}>{t("notasCredito.fecha")}</label>
            <input
              type="date"
              style={INPUT_STYLE}
              value={form.fecha}
              onChange={(e) => setField("fecha", e.target.value)}
            />
          </div>

          <div style={{ gridColumn: "span 2" }}>
            <label style={LABEL_STYLE}>{t("notasCredito.concepto")}</label>
            <textarea
              style={{ ...INPUT_STYLE, minHeight: 64, resize: "vertical" }}
              value={form.concepto}
              placeholder={t("notasCredito.conceptoPlaceholder")}
              onChange={(e) => setField("concepto", e.target.value)}
            />
          </div>

          <div style={{ gridColumn: "span 2" }}>
            <label style={LABEL_STYLE}>{t("notasCredito.monto")}</label>
            <input
              type="number"
              style={INPUT_STYLE}
              value={form.monto}
              min="0"
              step="0.01"
              onChange={(e) => setField("monto", e.target.value)}
            />
            {montoNum > 0 && !montoValido && (
              <p style={{ margin: "4px 0 0", fontSize: "0.75rem", color: "#f87171" }}>
                {t("notasCredito.montoSuperaSaldo", {
                  saldo: `${simbolo}${formatCurrency(saldoDisponible)}`,
                })}
              </p>
            )}
          </div>
        </div>

        {/* Preview de IVA */}
        {montoNum > 0 && (
          <div
            style={{
              marginTop: 14,
              background: "rgba(0,0,0,0.15)",
              borderRadius: 7,
              padding: "10px 12px",
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <Row label={t("notasCredito.baseGravable")} value={`${simbolo}${formatCurrency(base)}`} />
            <Row
              label={`${t("notasCredito.iva")} (${invoice.iva_incluido ? Number(invoice.tasa_iva) : 0}%)`}
              value={`${simbolo}${formatCurrency(iva)}`}
            />
            <div style={{ height: 1, background: "var(--border-card)", margin: "2px 0" }} />
            <Row label={t("notasCredito.totalAcreditado")} value={`${simbolo}${formatCurrency(total)}`} strong />
          </div>
        )}

        {error && <ErrorBox>{error}</ErrorBox>}

        <Divider />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <CancelBtn onClick={onClose} label={t("common.cancelar")} />
          <button
            onClick={handleSubmit}
            disabled={!montoValido || !conceptoValido || submitting}
            style={{
              padding: "8px 18px",
              background:
                !montoValido || !conceptoValido || submitting
                  ? "rgba(167,139,250,0.4)"
                  : "#a78bfa",
              color: "#fff",
              border: "none",
              borderRadius: 7,
              fontSize: "0.84rem",
              fontWeight: 600,
              cursor: !montoValido || !conceptoValido || submitting ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 7,
            }}
          >
            {submitting ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin" /> {t("common.guardando")}
              </>
            ) : (
              <>
                <i className="fa-solid fa-file-circle-minus" /> {t("notasCredito.emitir")}
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}

function MOTIVO_LABEL(motivo) {
  const found = MOTIVOS.find((m) => m.valor === motivo);
  return found ? found.labelKey : "notasCredito.motivoOtro";
}

// ─────────────────────────────────────────────────────────
// Helpers de UI (copiados de InvoiceActionsModal.js)
// ─────────────────────────────────────────────────────────
function Divider() {
  return <div style={{ borderTop: "1px solid var(--border-card)", margin: "16px 0" }} />;
}
function CloseBtn({ onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: "none",
        border: "none",
        color: "var(--text-muted)",
        cursor: "pointer",
        fontSize: "1.15rem",
        lineHeight: 1,
        padding: "2px 4px",
      }}
    >
      ✕
    </button>
  );
}
function CancelBtn({ onClick, label }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "8px 16px",
        background: "transparent",
        border: "1px solid var(--border-card)",
        borderRadius: 7,
        color: "var(--text-muted)",
        fontSize: "0.84rem",
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}
function WarningBox({ children, icon, color }) {
  return (
    <div
      style={{
        background: `${color}18`,
        border: `1px solid ${color}44`,
        borderLeft: `3px solid ${color}`,
        borderRadius: 6,
        padding: "9px 12px",
        color,
        fontSize: "0.83rem",
        marginBottom: 12,
        display: "flex",
        gap: 8,
        alignItems: "flex-start",
      }}
    >
      <i className={`fa-solid ${icon}`} style={{ marginTop: 2, flexShrink: 0 }} />
      <span>{children}</span>
    </div>
  );
}
function ErrorBox({ children }) {
  return (
    <div
      style={{
        background: "rgba(248,113,113,0.1)",
        border: "1px solid rgba(248,113,113,0.3)",
        borderLeft: "3px solid #f87171",
        borderRadius: 6,
        padding: "8px 12px",
        color: "#fca5a5",
        fontSize: "0.82rem",
        marginTop: 10,
      }}
    >
      <i className="fa-solid fa-circle-exclamation" style={{ marginRight: 5 }} />
      {children}
    </div>
  );
}
function Row({ label, value, strong }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <span
        style={{
          fontSize: strong ? "0.85rem" : "0.78rem",
          color: strong ? "var(--text-primary)" : "var(--text-muted)",
          fontWeight: strong ? 700 : 400,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: "monospace",
          fontWeight: strong ? 700 : 600,
          fontSize: strong ? "0.92rem" : "0.85rem",
          color: strong ? "#a78bfa" : "var(--text-secondary)",
        }}
      >
        {value}
      </span>
    </div>
  );
}
